import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Activity } from 'lucide-react';

export type LabelingTool = 'nvidia-monai';

export interface LabelingToolDef {
  id: LabelingTool;
  name: string;
  description: string;
  icon: 'Activity';
  features: string[];
  dataTypes: string[];
  integrations: string[];
}

export const LABELING_TOOLS: LabelingToolDef[] = [
  {
    id: 'nvidia-monai',
    name: 'MONAI Label',
    description: 'AI-powered medical image labeling and learning tool',
    icon: 'Activity',
    features: [
      'AI-assisted annotation (DeepEdit, DeepGrow, SAM2)',
      'Active learning with continuous model improvement',
      'Interactive segmentation with click-based input',
      'DICOMWeb/PACS connectivity',
    ],
    dataTypes: ['CT', 'MRI', 'DICOM', 'NIfTI', 'Pathology (TIFF/SVS)', 'Endoscopy'],
    integrations: ['3D Slicer', 'OHIF', 'QuPath', 'CVAT'],
  },
];

interface LabelConfigSelectorProps {
  selectedTool: LabelingTool;
  onToolChange: (tool: LabelingTool) => void;
  disabled?: boolean;
}

const getToolIcon = (icon: LabelingToolDef['icon']) => {
  switch (icon) {
    case 'Activity':
      return <Activity className="h-5 w-5 text-green-500" />;
    default:
      return null;
  }
};

const LabelConfigSelector = ({
  selectedTool,
  onToolChange,
  disabled = false,
}: LabelConfigSelectorProps) => {
  const selectedToolDef = LABELING_TOOLS.find((t) => t.id === selectedTool);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <Label className="text-base font-medium flex items-center gap-2">
            <Activity className="h-4 w-4" />
            Medical Imaging Platform
          </Label>
          <p className="text-sm text-muted-foreground">
            Deploy NVIDIA MONAI for medical image labeling and AI
          </p>
        </div>
        <Badge variant="secondary">Demo</Badge>
      </div>

      <RadioGroup
        value={selectedTool}
        onValueChange={(v) => onToolChange(v as LabelingTool)}
        disabled={disabled}
        className="grid grid-cols-1 gap-3"
      >
        {LABELING_TOOLS.map((tool) => (
          <Card
            key={tool.id}
            className={`cursor-pointer transition-colors ${
              selectedTool === tool.id
                ? 'border-primary bg-primary/5'
                : 'hover:border-muted-foreground/50'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={() => !disabled && onToolChange(tool.id)}
          >
            <CardContent className="flex flex-col items-center gap-2 p-4">
              <RadioGroupItem
                value={tool.id}
                id={`labeling-tool-${tool.id}`}
                disabled={disabled}
                className="sr-only"
              />
              <div className="flex items-center gap-2">
                {getToolIcon(tool.icon)}
                <label
                  htmlFor={`labeling-tool-${tool.id}`}
                  className="font-medium cursor-pointer"
                >
                  {tool.name}
                </label>
              </div>
              <p className="text-xs text-muted-foreground text-center">
                {tool.description}
              </p>
            </CardContent>
          </Card>
        ))}
      </RadioGroup>

      {/* Selected Tool Details */}
      {selectedToolDef && (
        <Card className="bg-muted/50">
          <CardContent className="pt-4 space-y-3">
            <div className="flex items-center gap-2">
              {getToolIcon(selectedToolDef.icon)}
              <span className="font-medium">{selectedToolDef.name}</span>
            </div>
            <div className="space-y-2">
              <div>
                <span className="text-xs text-muted-foreground">Supported Data Types</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {selectedToolDef.dataTypes.map((type) => (
                    <Badge key={type} variant="outline" className="text-xs">
                      {type}
                    </Badge>
                  ))}
                </div>
              </div>
              <div>
                <span className="text-xs text-muted-foreground">Features</span>
                <ul className="text-xs text-muted-foreground mt-1 space-y-0.5">
                  {selectedToolDef.features.map((feature) => (
                    <li key={feature}>• {feature}</li>
                  ))}
                </ul>
              </div>
              <div>
                <span className="text-xs text-muted-foreground">Viewer Integrations</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {selectedToolDef.integrations.map((integration) => (
                    <Badge key={integration} variant="secondary" className="text-xs">
                      {integration}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default LabelConfigSelector;
